import React, { useCallback, useState } from "react";
import swal from "sweetalert";
import { Link, NavLink } from "react-router-dom";
import {
  Button,
  Col,
  Dropdown,
  DropdownButton,
  FormControl,
  InputGroup,
  Row,
} from "react-bootstrap";
import { AddIconOutline, MenuDots, SearchIcon } from "../../components/SvgIcons";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { constants, customStyles } from "../../utils/constants";
import moment from "moment";
import { DeleteUser, GetUsers } from "../../services/users.service";
import DataTable from "react-data-table-component";
import BarLoader from "react-spinners/BarLoader";
import {
  EmptyLocalStorage,
  GetAuthUser,
} from "../../services/auth/auth.service";
import ThemeModal from "../../components/ThemeModal";
import {
  LIST_TABLE_SCROLL_HEIGHT,
  useInfinitePagedList,
  useListPageScrollLock,
} from "../../hooks/useInfinitePagedList";
import {
  LazyUserAvatar,
  ListLoadMoreIndicator,
} from "../../components/ListInfiniteHelpers";

function Agents() {
  const history = useHistory();
  const [keyword, setKeyword] = useState("");
  const current_user = GetAuthUser();
  const [imageModal, setImageModal] = useState({ url: "", status: false });

  useListPageScrollLock();

  const fetchPage = useCallback(
    async ({ page, pageSize, keyword: searchKeyword }) => {
      try {
        const result = await GetUsers(
          searchKeyword,
          constants.ROLES.ROLE_AGENT,
          page,
          pageSize
        );
        if (!result.success) {
          toast.error(result.message);
          return { rows: [], total: 0 };
        }
        const rows = (result.data || []).map((dt, index) => ({
          id: dt.id,
          index: (page - 1) * pageSize + index + 1,
          CategoryImg: dt.profile_image || constants.ClientImage,
          full_name: dt.full_name || "-",
          state: dt.state || "-",
          email: dt.email || "-",
          phone: dt.phone_number || "-",
          created_at: moment(dt.created_at).fromNow(),
        }));
        return { rows, total: result.total || 0 };
      } catch (error) {
        if (error.response?.status == 401) {
          EmptyLocalStorage();
          history.push("/");
        } else {
          toast.error(error.response?.data?.message || "Failed to load agents");
        }
        throw error;
      }
    },
    [history]
  );

  const {
    rows,
    loading,
    loadingMore,
    hasMore,
    totalRows,
    tableWrapperRef,
    refresh,
  } = useInfinitePagedList({
    fetchPage,
    keyword,
    resetDeps: [],
  });

  const clearInput = () => setKeyword("");

  const ModalForImage = () => (
    <div className={"modalBodyContent"} style={{ textAlign: "center" }}>
      <img
        style={{
          objectFit: "cover",
          objectPosition: "center",
          textAlign: "center",
        }}
        src={imageModal.url}
        className="img-fluid"
        alt=""
      />
    </div>
  );

  const onUserDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Are you sure you want to delete this user?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willShip) => {
      if (!willShip) return;
      await DeleteUser(id)
        .then((result) => {
          if (result.success) {
            swal(result.message, { icon: "success" });
            refresh();
          } else {
            toast.error(result.message);
          }
        })
        .catch((error) => {
          if (error.response?.status == 401) {
            EmptyLocalStorage();
            history.push("/");
          } else {
            return toast.error(error.response?.data?.message);
          }
        });
    });
  };

  const onEdit = (data) => {
    history.push("/manage-agents/edit-agent/" + data.id, { data });
  };

  const userActions = (data) => (
    <DropdownButton
      align="end"
      className={"actionDropdown"}
      id="dropdown-item-button"
      title={<MenuDots />}
    >
      <Dropdown.Item as="button" onClick={() => onEdit(data)}>
        Edit
      </Dropdown.Item>
      {current_user?.role === constants.ROLES.ROLE_ADMIN && (
        <Dropdown.Item as="button" onClick={() => onUserDelete(data.id)}>
          Delete
        </Dropdown.Item>
      )}
    </DropdownButton>
  );

  const columns = [
    {
      id: "CategoryImg",
      name: "Picture",
      width: "100px",
      selector: (rowData) => rowData.oid,
      format: (rowData) => (
        <LazyUserAvatar
          src={rowData.CategoryImg}
          onClick={() =>
            setImageModal({ url: rowData.CategoryImg, status: true })
          }
        />
      ),
    },
    {
      id: "full_name",
      name: "Agents Name",
      maxWidth: "160px",
      selector: (rowData) => rowData.full_name,
      format: (rowData) =>
        current_user?.role === constants.ROLES.ROLE_ADMIN ? (
          <NavLink
            className="text-dark text-decoration-none"
            to={`/manage-agents/edit-agent/${rowData.id}`}
          >
            {rowData.full_name}
          </NavLink>
        ) : (
          rowData.full_name
        ),
    },
    {
      id: "email",
      name: "Email",
      cell: (rowData) => rowData.email,
    },
    {
      id: "phone",
      name: "Phone",
      selector: (rowData) => (rowData.phone || "").replace("+", ""),
    },
    {
      id: "State_here",
      name: "State",
      selector: (rowData) => rowData.state,
    },
    current_user?.role === constants.ROLES.ROLE_ADMIN && {
      id: "action",
      name: "Action",
      cell: (rowData) => userActions(rowData),
    },
  ].filter(Boolean);

  return (
    <div className={"usersPage users-page-fixed"}>
      <h3 className="page-heading">Manage Agents</h3>
      <hr />
      <div className={"customOrderFilters users-page-filters"}>
        <Row>
          <Col xs={12} sm={12} md={5} lg={5} xl={5}>
            <InputGroup className="mb-3 search-group">
              <InputGroup.Text id="basic-addon1">
                <SearchIcon />
              </InputGroup.Text>
              <FormControl
                placeholder="Search"
                className="custom-search"
                aria-label="Search"
                maxLength={"255"}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                style={{
                  borderRight: keyword ? "none" : "1px solid #ced4da",
                  borderTopRightRadius: keyword ? "0" : "5px",
                  borderBottomRightRadius: keyword ? "0" : "5px",
                }}
              />
              {keyword && (
                <InputGroup.Text
                  className="cross-icon"
                  id="basic-addon2"
                  onClick={clearInput}
                >
                  <svg
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 6.48828L18 18.4883"
                      stroke="#817A8E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18 6.48828L6 18.4883"
                      stroke="#817A8E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </InputGroup.Text>
              )}
            </InputGroup>
          </Col>
          <Col xs={12} sm={12} md={7} lg={7} xl={7}>
            <Row>
              <Col xs={12} sm={12} md={3} lg={3} xl={3}></Col>
              <Col xs={12} sm={12} md={5} lg={5} xl={5}></Col>
              <Col xs={12} sm={12} md={6} lg={4} xl={4}>
                <Link to={"/manage-agents/add-agent"}>
                  <Button className={"btn btn-green-theme h40 w-100"}>
                    <AddIconOutline /> Add New
                  </Button>
                </Link>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <div
        className="theme-dataGrid products-dataGrid custom-table-border user-table users-infinite-table mt-3"
        ref={tableWrapperRef}
      >
        <DataTable
          columns={columns}
          data={rows}
          progressPending={loading}
          customStyles={customStyles}
          selectableRows={false}
          striped
          highlightOnHover
          pagination={false}
          persistTableHead
          fixedHeader
          fixedHeaderScrollHeight={LIST_TABLE_SCROLL_HEIGHT}
          progressComponent={
            <BarLoader
              color={"#961b1a"}
              loading={loading}
              css={"marginTop: 10px"}
              height={"4"}
              width={"100%"}
            />
          }
        />
        <ListLoadMoreIndicator
          loadingMore={loadingMore}
          hasMore={hasMore}
          totalRows={totalRows}
          entityLabel="agents"
        />
      </div>
      <ThemeModal
        title={""}
        size={"md"}
        content={<ModalForImage />}
        show={imageModal.status}
        onHide={() => setImageModal({ url: "", status: false })}
      />
    </div>
  );
}

export default Agents;
